/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useCompanyPageStore } from "../../../../../store/companyPageStore";
import { useQuery } from "@apollo/client";
import { COMPANY_COUNTRY_SUMMARY_QUERY } from "../../../../../legacy/queries/Companies";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_worldLow from "@amcharts/amcharts4-geodata/worldLow";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { SpogCompany } from "../../../../../legacy/types/SpogCompany";
import Stack from "@mui/material/Stack";
import { COMPANY_SEARCH_CLIENT } from "../../../../../utils/consts";

am4core.useTheme(am4themes_animated);

const buildMapData = (companies: SpogCompany[]) => {
  const countryMap = new Map<string, { name: string; count: number }>();

  companies.forEach((company) => {
    const country = company.locationPrimary?.state?.country;
    if (country?.alpha2 && country?.name) {
      const existing = countryMap.get(country.alpha2);
      if (existing) {
        existing.count += 1;
      } else {
        countryMap.set(country.alpha2, {
          name: country.name,
          count: 1,
        });
      }
    }
  });

  return Array.from(countryMap.entries()).map(([alpha2, data]) => ({
    id: alpha2,
    name: data.name,
    value: data.count,
    color: "#FF6B6B",
  }));
};

export const CompanyCountByCountryChart = React.memo(() => {
  const company = useCompanyPageStore((state) => state.company);
  const mapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<am4maps.MapChart | null>(null);

  const { data, loading, error } = useQuery<{
    search: { data: SpogCompany[] };
  }>(COMPANY_COUNTRY_SUMMARY_QUERY, {
    variables: {
      filters: {
        hgIdsIncludingCorporateRelatives: company?.hgId,
      },
    },
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
    },
    skip: !company?.hgId,
  });

  const mapData = useMemo(
    () => (data?.search?.data ? buildMapData(data.search.data) : []),
    [data?.search?.data]
  );

  useEffect(() => {
    if (!mapRef.current || !mapData.length) return;

    // Create map instance
    const chart = am4core.create(mapRef.current, am4maps.MapChart);
    chartRef.current = chart;

    // Set map definition
    chart.geodata = am4geodata_worldLow;

    // Set projection
    chart.projection = new am4maps.projections.Miller();

    // Create map polygon series
    const polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.exclude = ["AQ"];
    polygonSeries.useGeodata = true;
    polygonSeries.nonScalingStroke = true;
    polygonSeries.strokeWidth = 0.5;
    polygonSeries.calculateVisualCenter = true;
    polygonSeries.mapPolygons.template.fill = am4core.color("#E8E8E8");
    polygonSeries.mapPolygons.template.stroke = am4core.color("#FFFFFF");

    // Invalidate image series when polygon series is validated
    polygonSeries.events.on("validated", function () {
      imageSeries.invalidate();
    });

    // Create image series for bubbles
    const imageSeries = chart.series.push(new am4maps.MapImageSeries());
    imageSeries.data = mapData;
    imageSeries.dataFields.value = "value";

    const imageTemplate = imageSeries.mapImages.template;
    imageTemplate.nonScaling = true;

    // Use adapters to position bubbles on country centers
    imageTemplate.adapter.add("latitude", function (latitude, target) {
      const polygon = polygonSeries.getPolygonById(
        (target.dataItem.dataContext as any).id
      );
      if (polygon) {
        return polygon.visualLatitude;
      }
      return latitude;
    });

    imageTemplate.adapter.add("longitude", function (longitude, target) {
      const polygon = polygonSeries.getPolygonById(
        (target.dataItem.dataContext as any).id
      );
      if (polygon) {
        return polygon.visualLongitude;
      }
      return longitude;
    });

    // Create circle for bubbles
    const circle = imageTemplate.createChild(am4core.Circle);
    circle.fillOpacity = 0.8;
    circle.fill = am4core.color("#4285F4");
    circle.stroke = am4core.color("#FFFFFF");
    circle.strokeWidth = 2;
    circle.tooltipText = "[bold]{name}: {value} companies [/]";

    // Set up heat rules for bubble sizing
    imageSeries.heatRules.push({
      target: circle,
      property: "radius",
      min: 8,
      max: 30,
      dataField: "value",
    });

    // Add hover effects
    const hoverState = circle.states.create("hover");
    hoverState.properties.scale = 1.2;
    hoverState.properties.fill = am4core.color("#3367D6");

    // Add small map
    chart.smallMap = new am4maps.SmallMap();
    chart.smallMap.series.push(polygonSeries);

    return () => {
      // Async disposal to prevent UI blocking
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.dispose();
          chartRef.current = null;
        }
      }, 0);
    };
  }, [mapData]);

  if (!company) return null;

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          Company Count by Country
        </Typography>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          Company Count by Country
        </Typography>
        <Stack
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height={400}
        >
          <Typography color="error">Error loading data</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        Company Count by Country
      </Typography>
      <Box sx={{ height: 300, width: "66%" }}>
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      </Box>
    </Box>
  );
});

CompanyCountByCountryChart.displayName = "CompanyCountByCountryChart";
